import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { apiFetch } from "../lib/api";

interface RelationshipToMe {
  fromPersonId: string | null;
  fromPersonName: string | null;
  matchedBy: "link" | "name" | null;
  toPersonId: string;
  description: string | null;
  label: string | null;
  pathPersonIds: string[];
  reason?: "not_linked" | "no_path" | "self";
}

/**
 * "Your first cousin once removed" under a relative's name.
 *
 * Computed from the tree, not by a model — instant and always consistent
 * with the family AI, which answers the same question from the same code.
 */
export function RelationshipToMeLine({
  familySlug,
  personId,
}: {
  familySlug: string;
  personId: string;
}) {
  const query = useQuery<RelationshipToMe>({
    queryKey: ["relationship-to-me", familySlug, personId],
    queryFn: () =>
      apiFetch<RelationshipToMe>(
        `/families/${familySlug}/persons/${personId}/relationship-to-me`,
      ),
  });

  if (query.isLoading || query.error || !query.data) return null;
  const rel = query.data;

  const base: React.CSSProperties = {
    fontSize: "0.95rem",
    margin: "8px 0 0",
    color: "var(--c-text-subtle)",
  };

  if (rel.reason === "not_linked") {
    return (
      <p style={base}>
        <Link to="/profile" style={{ color: "var(--c-blue)" }}>
          Tell us who you are in the tree
        </Link>{" "}
        to see how you're related.
      </p>
    );
  }
  if (rel.reason === "self") {
    return <p style={{ ...base, fontWeight: 600 }}>This is you</p>;
  }
  if (!rel.label) return null;

  return (
    <p style={{ ...base, color: "var(--c-text)" }}>
      <strong>{rel.label}</strong>
      {rel.matchedBy === "name" && rel.fromPersonName && (
        <span className="text-subtle">
          {" "}
          (assuming you're {rel.fromPersonName})
        </span>
      )}
      {rel.pathPersonIds.length > 1 && (
        <>
          {" · "}
          <Link
            to={`/tree?path=${rel.pathPersonIds.join(",")}`}
            style={{ color: "var(--c-blue)" }}
          >
            Show on tree
          </Link>
        </>
      )}
    </p>
  );
}
